import { orderBy, sumBy, uniqueId } from 'lodash';
import { TechnologyTypeCode } from '../technologyTypeCode';
import { UnitExtraPartFormModel } from '../quote-or-design-form/units/unitExtraPartFormModel';
import { BuildTypeCode } from '../../projects/project';
import { HeatPumpResponse, mapHeatPumpResponseToFormModel } from '../heat-pump/heatPumpResponse';
import {
  getTotalUnderfloorHeatingAreaForManifoldInMetresSquared,
  ManifoldResponse,
  mapManifoldResponseToFormModel,
} from '../manifolds/manifoldResponse';
import { DataForQuoteOrDesignForm, PumpFormModel } from '../quote-or-design-form/GetDataForQuoteOrDesignForm';
import {
  PumpLocationCode,
  PumpSelectionTypeCode,
  UnitHeatSourceTypeCode,
} from '../quote-or-design-form/quoteOrDesignFormData';
import { UnitFormModel } from '../quote-or-design-form/units/unitFormModel';
import { mapSolarResponseToFormModel, SolarResponse } from '../solar/solarResponse';
import {
  mapUnitHeatLossDetailsResponseToFormModel,
  UnitHeatLossDetailsResponse,
} from './heatLoss/unitHeatLossDetailsResponse';

export type UnitResponse = {
  unitId: number;
  name: string;
  displayIndex: number;
  quantity: number;
  numberOfBedrooms: number | null;
  numberOfBathrooms: number | null;
  cylinderVolumeOption: string | null;
  hasPipelineMap: boolean;
  pipelineMapJson: string;
  waterBufferSizeInLitres: number | null;
  boilerCapacityInKilowatts: number | null;
  pumpSelectionTypeCode: PumpSelectionTypeCode;
  pumpLocationCode: PumpLocationCode;
  pumpPartIds: Array<number>;
  extraParts: Array<UnitExtraPartResponse>;
  heatSourceTypeCode: UnitHeatSourceTypeCode | null;
  heatPump: HeatPumpResponse | null;
  solar: SolarResponse | null;
  manifolds: Array<ManifoldResponse>;
  heatLossDetails: UnitHeatLossDetailsResponse | null;
};

export type UnitExtraPartResponse = {
  partId: number;
  technologyTypeCode: TechnologyTypeCode;
  quantity: number;
};

const getExtraPartsForTechnology = (
  extraParts: Array<UnitExtraPartResponse>,
  technologyTypeCode: TechnologyTypeCode,
): Array<UnitExtraPartFormModel> =>
  extraParts
    .filter(extraPart => extraPart.technologyTypeCode === technologyTypeCode)
    .map(extraPart => ({
      partId: extraPart.partId,
      quantity: extraPart.quantity.toString(),
    }));

export const mapUnitResponseToFormModel = ({
  unitResponse,
  referenceData,
  projectBuildType,
  mapRoomlessManifoldDetailsToRoom,
  createDesignDetails,
  overrideHasPipelineMapToTrue = false,
}: {
  unitResponse: UnitResponse;
  referenceData: DataForQuoteOrDesignForm;
  projectBuildType: BuildTypeCode;
  mapRoomlessManifoldDetailsToRoom: boolean;
  createDesignDetails: boolean;
  overrideHasPipelineMapToTrue?: boolean;
}): UnitFormModel => ({
  unitId: unitResponse.unitId,
  id: uniqueId('unit'),
  isExpanded: true,
  totalUnderfloorHeatingAreaInMetresSquared: getTotalUnderfloorHeatingAreaForUnitInMetresSquared(
    unitResponse,
    referenceData,
  ),
  name: unitResponse.name,
  quantity: unitResponse.quantity.toString(),
  numberOfBedrooms: unitResponse.numberOfBedrooms?.toString() ?? '',
  numberOfBathrooms: unitResponse.numberOfBathrooms?.toString() ?? '',
  cylinderVolumeOption: unitResponse.cylinderVolumeOption,
  hasPipelineMap: overrideHasPipelineMapToTrue || unitResponse.hasPipelineMap,
  pipelineMapJson: unitResponse.pipelineMapJson,
  waterBufferSizeInLitres: unitResponse.waterBufferSizeInLitres,
  boilerCapacityInKilowatts: unitResponse.boilerCapacityInKilowatts,
  pumpSelectionType: unitResponse.pumpSelectionTypeCode,
  pumpLocation: unitResponse.pumpLocationCode,
  pumps: getUnitPumps(unitResponse.pumpPartIds, referenceData),
  underfloorHeatingExtraParts: getExtraPartsForTechnology(unitResponse.extraParts, 'UnderfloorHeating'),
  heatPumpExtraParts: getExtraPartsForTechnology(unitResponse.extraParts, 'HeatPump'),
  heatSourceTypeCode: unitResponse.heatSourceTypeCode,
  heatPump:
    unitResponse.heatPump != null
      ? mapHeatPumpResponseToFormModel(unitResponse.heatPump, referenceData, projectBuildType)
      : null,
  solar: unitResponse.solar != null ? mapSolarResponseToFormModel(unitResponse.solar) : null,
  manifolds: orderBy(unitResponse.manifolds, manifold => manifold.displayIndex).map(manifoldResponse =>
    mapManifoldResponseToFormModel({
      manifoldResponse,
      referenceData,
      mapRoomlessManifoldDetailsToRoom,
      createDesignDetails,
      unitResponse,
      buildTypeCode: projectBuildType,
    }),
  ),
  heatLossDetails:
    unitResponse.heatLossDetails == null
      ? null
      : mapUnitHeatLossDetailsResponseToFormModel(unitResponse.heatLossDetails),
  designViewType: null,
});

const getTotalUnderfloorHeatingAreaForUnitInMetresSquared = (
  unitResponse: UnitResponse,
  parts: DataForQuoteOrDesignForm,
) =>
  sumBy(
    unitResponse.manifolds,
    manifoldResponse => getTotalUnderfloorHeatingAreaForManifoldInMetresSquared(manifoldResponse, parts) ?? 0,
  );

const getUnitPumps = (pumpPartIds: Array<number>, parts: DataForQuoteOrDesignForm): Array<PumpFormModel | null> => {
  const pumps = parts.unitPumpOptions;

  const matchedPumps = pumpPartIds
    .map(partId => pumps.find(pump => pump.value != null && pump.value.partId === partId) || null)
    .map(pump => (pump == null ? null : pump.value));

  return matchedPumps.length !== 0 ? matchedPumps : [null];
};
